// uiarc: empty-state (https://uiarc.dev/r/empty-state.json) — CSS Modules yerine Tailwind. Simge kutusu ilk
// göründüğünde bir kez oturur; sonraki değişimlerde simge yerinde çapraz geçer, başlık ve açıklama bulanıklıktan
// yükselirken eskisi yukarı çıkar, metin bloğunun yüksekliği yayla izlenir. Metin dengeli satırlara bölünür
// (tek sözcük asılı kalmaz). `prefers-reduced-motion` altında yalnız solma.
import { Folder } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { isValidElement, type ReactNode } from 'react'

import { iconSwap, textSwap } from '@/components/ui/morph-copy-motion'
import { HeightFrame, Swap } from '@/components/ui/morph-copy'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

interface EmptyStateProps {
  title: string
  description: string
  action?: ReactNode
  icon?: ReactNode
  className?: string
  /** Durum bölgesinin erişilebilir adı. */
  label?: string
}

/** Yeni bir simge bileşeni çapraz geçer; aynı simgenin yeniden çizilmesi yerinde kalır. */
function iconKey(icon: ReactNode) {
  if (!isValidElement(icon)) return 'icon'
  const type = icon.type as string | { displayName?: string; name?: string }
  return typeof type === 'string' ? type : (type.displayName ?? type.name ?? 'icon')
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
  label,
}: EmptyStateProps) {
  const reduce = useReducedMotion()
  const glyph = icon ?? <Folder className="size-6" strokeWidth={1.5} />
  const swap = textSwap(reduce)
  // Eylemin sonucu yerinde dönüşür: simge çapraz geçer, metin yükselirken eskisi çıkar.
  return (
    <section
      data-slot="empty-state"
      className={cn(
        'flex w-full flex-col items-center px-5 py-[clamp(2rem,8vw,3rem)] text-center tracking-(--tracking-body)',
        className,
      )}
      aria-label={label}
    >
      <motion.div
        className="border-border bg-surface-muted text-text-secondary rounded-panel relative grid size-12 place-items-center border"
        aria-hidden="true"
        initial={reduce ? false : { opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: motionTokens.duration.considered, ease: motionTokens.ease.enter }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <Swap key={iconKey(glyph)} className="grid place-items-center" {...iconSwap(reduce)}>
            {glyph}
          </Swap>
        </AnimatePresence>
      </motion.div>
      <HeightFrame
        className="w-full max-w-full"
        contentClassName="flow-root"
        reduce={reduce}
        morphKey={`${title}\n${description}`}
      >
        <h3 className="relative mt-5 text-base [line-height:1.1] font-medium tracking-(--tracking-display)">
          <AnimatePresence mode="popLayout" initial={false}>
            <Swap key={title} className="block text-balance" {...swap}>
              {title}
            </Swap>
          </AnimatePresence>
        </h3>
        <p className="text-text-secondary relative mx-auto mt-2 max-w-72 text-sm [line-height:var(--leading-body)]">
          <AnimatePresence mode="popLayout" initial={false}>
            <Swap key={description} className="block text-balance" {...swap}>
              {description}
            </Swap>
          </AnimatePresence>
        </p>
      </HeightFrame>
      {action && <div className="mt-5 flex flex-wrap justify-center gap-3">{action}</div>}
    </section>
  )
}
